/**
 * StochasticSubpanel.jsx
 *
 * Lightweight SVG subpanel rendering %K + %D lines, overbought/oversold
 * horizontal levels, shaded neutral zone, and a live snapshot label.
 * ~80px tall (fits mobile & desktop). Reactive to `viewRange` so the
 * x-axis stays aligned with the main heatmap canvas — same candle
 * window, same normalized coordinates.
 */
import React, { useMemo } from "react";
import { computeStochastic, stochasticSnapshot } from "./stochasticMath";
import { tLm } from "../../i18n/liqmap";

const PANEL_HEIGHT = 80;
const PAD_TOP = 8;
const PAD_BOTTOM = 12;
const PAD_LEFT = 6;
const PAD_RIGHT = 60; // reserve space for right-side label

const COLOR_K = "#f59e0b";        // %K amber
const COLOR_D = "#94a3b8";        // %D slate
const COLOR_OVERBOUGHT = "#ef4444";
const COLOR_OVERSOLD = "#10b981";
const COLOR_NEUTRAL_FILL = "rgba(148, 163, 184, 0.05)";
const COLOR_GRID = "rgba(255, 255, 255, 0.05)";

export default function StochasticSubpanel({
  candles = [],
  lang = "es",
  viewStart = null,
  viewEnd = null,
}) {
  const t = tLm(lang);
  const { k, d } = useMemo(() => computeStochastic(candles), [candles]);
  const snapshot = useMemo(() => stochasticSnapshot(k, d), [k, d]);

  // Determine visible slice of candles — mirrors the main chart's brush
  // window. If viewStart/viewEnd not provided, use the full array.
  const n = candles.length;
  const iStart = Math.max(0, Math.min(n - 1, viewStart ?? 0));
  const iEnd = Math.max(iStart + 1, Math.min(n, viewEnd ?? n));
  const visibleN = Math.max(2, iEnd - iStart);

  // Build SVG path strings for %K and %D over the visible window.
  const buildPath = (arr) => {
    let path = "";
    let started = false;
    for (let i = iStart; i < iEnd; i++) {
      const v = arr[i];
      if (!Number.isFinite(v)) continue;
      const xNorm = (i - iStart) / (visibleN - 1);
      const yNorm = 1 - v / 100; // 0..100 → top(0)..bottom(1)
      const cmd = started ? "L" : "M";
      path += `${cmd}${xNorm.toFixed(5)} ${yNorm.toFixed(5)}`;
      started = true;
    }
    return path || null;
  };

  const kPath = useMemo(() => buildPath(k), [k, iStart, iEnd, buildPath]);
  const dPath = useMemo(() => buildPath(d), [d, iStart, iEnd, buildPath]);

  const zoneLabel = (() => {
    if (!snapshot.zone) return null;
    if (snapshot.zone === "overbought") return t.stochastic?.overbought || "SOBRECOMPRA";
    if (snapshot.zone === "oversold")   return t.stochastic?.oversold   || "SOBREVENTA";
    return t.stochastic?.neutral || "NEUTRO";
  })();
  const zoneColor = snapshot.zone === "overbought"
    ? COLOR_OVERBOUGHT
    : snapshot.zone === "oversold"
      ? COLOR_OVERSOLD
      : "rgba(255,255,255,0.55)";

  return (
    <div
      data-testid="stochastic-subpanel"
      className="w-full rounded-md border"
      style={{
        borderColor: "rgba(245,158,11,0.25)",
        background: "rgba(8,5,8,0.6)",
        height: PANEL_HEIGHT,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* SVG plot — normalized 0..1 in both axes then scaled via viewBox */}
      <svg
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        style={{
          position: "absolute",
          top: PAD_TOP,
          left: PAD_LEFT,
          width: `calc(100% - ${PAD_LEFT + PAD_RIGHT}px)`,
          height: PANEL_HEIGHT - PAD_TOP - PAD_BOTTOM,
        }}
      >
        {/* Neutral zone fill 20-80 */}
        <rect x="0" y="0.2" width="1" height="0.6" fill={COLOR_NEUTRAL_FILL} />
        {/* Grid: middle line at 50 */}
        <line x1="0" y1="0.5" x2="1" y2="0.5" stroke={COLOR_GRID} strokeWidth="0.005" />
        {/* Overbought line (80) — dashed red */}
        <line x1="0" y1="0.2" x2="1" y2="0.2" stroke={COLOR_OVERBOUGHT} strokeWidth="0.006" strokeDasharray="0.015 0.01" opacity="0.55" />
        {/* Oversold line (20) — dashed green */}
        <line x1="0" y1="0.8" x2="1" y2="0.8" stroke={COLOR_OVERSOLD} strokeWidth="0.006" strokeDasharray="0.015 0.01" opacity="0.55" />
        {/* %D signal line (drawn first so %K sits on top) */}
        {dPath && (
          <path d={dPath} fill="none" stroke={COLOR_D} strokeWidth="0.008" vectorEffect="non-scaling-stroke" opacity="0.8" />
        )}
        {/* %K line */}
        {kPath && (
          <path d={kPath} fill="none" stroke={COLOR_K} strokeWidth="0.010" vectorEffect="non-scaling-stroke" />
        )}
      </svg>

      {/* Right-side numeric label + zone badge */}
      <div
        className="absolute font-mono"
        style={{ right: 8, top: 6, fontSize: 10, color: "rgba(255,255,255,0.85)", lineHeight: 1.2 }}
        data-testid="stochastic-snapshot"
      >
        <div>
          <span style={{ color: COLOR_K }}>%K</span>: {Number.isFinite(snapshot.k) ? snapshot.k.toFixed(1) : "—"}
        </div>
        <div>
          <span style={{ color: COLOR_D }}>%D</span>: {Number.isFinite(snapshot.d) ? snapshot.d.toFixed(1) : "—"}
        </div>
        {zoneLabel && (
          <div
            data-testid="stochastic-zone-badge"
            style={{
              marginTop: 2,
              padding: "1px 5px",
              fontSize: 8,
              fontWeight: 700,
              letterSpacing: 1,
              color: zoneColor,
              border: `1px solid ${zoneColor}`,
              borderRadius: 3,
              display: "inline-block",
            }}
          >
            {zoneLabel}
          </div>
        )}
      </div>

      {/* Left-side scale ticks (0/50/100) */}
      <div
        className="absolute font-mono"
        style={{ left: 4, top: 2, fontSize: 8, color: "rgba(255,255,255,0.35)", lineHeight: 1 }}
      >
        <div style={{ position: "absolute", top: PAD_TOP - 4 }}>100</div>
        <div style={{ position: "absolute", top: PANEL_HEIGHT / 2 - 6 }}>50</div>
        <div style={{ position: "absolute", top: PANEL_HEIGHT - PAD_BOTTOM - 6 }}>0</div>
      </div>

      {/* Title label — bottom-left, low contrast */}
      <div
        className="absolute font-mono"
        style={{
          left: 24, bottom: 2, fontSize: 8, color: "rgba(245,158,11,0.55)",
          letterSpacing: 1.5, fontWeight: 700,
        }}
        data-testid="stochastic-title"
      >
        {(t.toolbar?.stochastic || "Estocástico").replace(/^[^\w]+/, "")} 14·3·3
      </div>
    </div>
  );
}
